import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import {
  createFileRoute,
  Link,
  redirect,
  useRouter,
} from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormItemLayout,
  FormLabel,
} from '@/components/ui/form'
import { Input, InputPassword } from '@/components/ui/input'
import { AuthCard } from '@/components/view/auth-view'

import { authRegister } from '@/services/auth/auth.gql'
import { useAuth } from '@/services/auth/auth.store'
import { profileIsConfirmed } from '@/services/profile/profile.util'

import { GraphQLError } from '@/lib/gql-fetch'

export const Route = createFileRoute('/auth/register')({
  async loader({ context }) {
    const { isAuthenticated } = useAuth.getState()
    if (!isAuthenticated) return

    if (await profileIsConfirmed()) {
      throw redirect({ to: '/', replace: true })
    }

    throw redirect({ to: '/auth/verification', replace: true })
  },
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <AuthCard
      title="Daftar"
      desc="Bergabung dan tingkatkan potensi karir masa depanmu"
    >
      <RegisterForm />
    </AuthCard>
  )
}

/* ---------------------------------- FORM ---------------------------------- */

type RegisterDto = z.infer<typeof RegisterDto>
const RegisterDto = z
  .object({
    name: z.string().min(3, { message: 'Nama harus lebih dari 3 karakter' }),
    email: z
      .string()
      .min(1, { message: 'Email wajib diisi' })
      .email({ message: 'Email tidak valid' }),
    emailConfirmation: z
      .string()
      .min(1, { message: 'Email konfirmasi wajib diisi' })
      .email({ message: 'Email konfirmasi tidak valid' }),
    whatsapp: z
      .string()
      .regex(/^\d+$/, 'Nomor whatsapp harus berupa angka')
      .min(10, 'Phone number must be at least 10 digits')
      .max(15, 'Phone number must be at most 15 digits')
      .refine((x) => x.startsWith('62'), 'Nomor whatsapp dimulai dengan 62'),
    password: z.string().min(1, { message: 'Password wajib diisi' }),
    passwordConfirmation: z
      .string()
      .min(1, { message: 'Password konfirmasi wajib diisi' }),
    aggreement: z.boolean(),
  })
  .superRefine(
    ({ email, emailConfirmation, password, passwordConfirmation }, ctx) => {
      if (!!email && email !== emailConfirmation) {
        ctx.addIssue({
          code: 'custom',
          path: ['emailConfirmation'],
          message: 'Email konfirmasi harus sama',
        })
      }
      if (!!password && password !== passwordConfirmation) {
        ctx.addIssue({
          code: 'custom',
          path: ['passwordConfirmation'],
          message: 'Password konfirmasi harus sama',
        })
      }
    }
  )

function RegisterForm() {
  const router = useRouter()
  const navigate = Route.useNavigate()
  const login = useAuth((s) => s.login)
  const form = useForm<RegisterDto>({
    resolver: zodResolver(RegisterDto),
    defaultValues: getDefaultValues(),
  })

  const { mutateAsync } = useMutation({
    mutationFn: async (data: RegisterDto) => {
      const result = await authRegister.api({
        request: {
          email: data.email,
          name: data.name,
          password: data.password,
          phone: data.whatsapp,
          via: 'email',
        },
      })

      await login(result.register.token)
      router.invalidate()
    },
    onSuccess: (data) => {
      navigate({ to: '/auth/verification', replace: true })
    },
    onError: (error) => {
      if (error instanceof GraphQLError) {
        if (error.codes.includes(409)) {
          form.setError('email', { message: 'Email sudah terdaftar' })
        }
      }
    },
  })

  const handleSubmit = form.handleSubmit((data) => {
    toast.promise(mutateAsync(data), {
      loading: 'Mendaftarkan akun',
      success: 'Berhasil mendaftarkan akun',
      error: 'Gagal mendaftarkan akun',
    })
  }, console.error)

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit}>
        <fieldset className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItemLayout label="Nama" className="w-full">
                  <Input
                    placeholder="Masukkan nama lengkap"
                    autoComplete="username"
                    {...field}
                  />
                </FormItemLayout>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItemLayout label="Email" className="min-w-[250px] grow">
                  <Input placeholder="Masukkan email" {...field} type="email" />
                </FormItemLayout>
              )}
            />
            <FormField
              control={form.control}
              name="emailConfirmation"
              render={({ field }) => (
                <FormItemLayout
                  label="Konfirmasi Email"
                  className="min-w-[250px] grow"
                >
                  <Input
                    placeholder="Masukkan ulang email"
                    type="email"
                    {...field}
                  />
                </FormItemLayout>
              )}
            />

            <FormField
              control={form.control}
              name="whatsapp"
              render={({ field }) => (
                <FormItemLayout label="Nomor aktif whatsapp" className="w-full">
                  <Input placeholder="Masukkan nomor hp" {...field} />
                </FormItemLayout>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItemLayout label="Password" className="min-w-[250px] grow">
                  <InputPassword placeholder="Masukkan Password" {...field} />
                </FormItemLayout>
              )}
            />
            <FormField
              control={form.control}
              name="passwordConfirmation"
              render={({ field }) => (
                <FormItemLayout
                  label="Konfirmasi Password"
                  className="min-w-[250px] grow"
                >
                  <InputPassword
                    placeholder="Masukkan ulang password"
                    {...field}
                  />
                </FormItemLayout>
              )}
            />
            <FormField
              control={form.control}
              name="aggreement"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-2 space-y-0 pt-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        required
                      />
                    </FormControl>
                    <FormLabel className="block pt-0.5 text-sm">
                      Saya menyetujui peraturan yang berlaku
                    </FormLabel>
                  </div>
                </FormItem>
              )}
            />
          </div>
          <Button variant="yes" className="w-full font-bold">
            Daftar Sekarang
          </Button>
        </fieldset>
      </form>
      <p className="w-full text-center text-sm">
        Email sudah terdaftar? Silakan&nbsp;
        <Link
          className="text-persian-red-600 text-sm font-bold hover:underline"
          from={Route.fullPath}
          to="/auth/login"
        >
          Masuk
        </Link>
      </p>
    </Form>
  )
}

function getDefaultValues(): RegisterDto {
  return {
    name: '',
    email: '',
    emailConfirmation: '',
    whatsapp: '',
    password: '',
    passwordConfirmation: '',
    aggreement: false,
  }
}
